<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const count = key => Number(props.metrics?.[key] || 0);

const audience = computed(() => count('audience'));

const segments = computed(() => {
  const read = count('read');
  const delivered = Math.max(count('delivered') - read, 0);
  const failed = count('failed');
  const skipped = count('skipped');
  const pending = Math.max(
    audience.value - read - delivered - failed - skipped,
    0
  );

  return [
    {
      key: 'read',
      label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.LEGEND.READ'),
      value: read,
      barClass: 'fill-n-iris-9',
      dotClass: 'bg-n-iris-9',
    },
    {
      key: 'delivered',
      label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.LEGEND.DELIVERED'),
      value: delivered,
      barClass: 'fill-n-teal-9',
      dotClass: 'bg-n-teal-9',
    },
    {
      key: 'failed',
      label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.LEGEND.FAILED'),
      value: failed,
      barClass: 'fill-n-ruby-9',
      dotClass: 'bg-n-ruby-9',
    },
    {
      key: 'skipped',
      label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.LEGEND.SKIPPED'),
      value: skipped,
      barClass: 'fill-n-amber-9',
      dotClass: 'bg-n-amber-9',
    },
    {
      key: 'pending',
      label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.LEGEND.PENDING'),
      value: pending,
      barClass: 'fill-n-slate-6',
      dotClass: 'bg-n-slate-6',
    },
  ];
});

const chartSegments = computed(() => {
  let offset = 0;

  return segments.value.map(segment => {
    const width = audience.value ? (segment.value / audience.value) * 100 : 0;
    const chartSegment = { ...segment, offset, width };
    offset += width;
    return chartSegment;
  });
});

const deliveryRate = computed(() =>
  audience.value ? Math.round((count('delivered') / audience.value) * 100) : 0
);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 border rounded-xl bg-n-solid-1 border-n-weak"
  >
    <div class="flex items-center justify-between gap-3">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.TITLE') }}
      </span>
      <span class="text-sm tabular-nums text-n-slate-11">
        {{
          t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.DELIVERY_RATE', {
            value: deliveryRate,
          })
        }}
      </span>
    </div>
    <div
      v-if="loading"
      class="w-full h-2 rounded-full bg-n-slate-3 animate-pulse"
    />
    <svg
      v-else-if="audience"
      viewBox="0 0 100 8"
      preserveAspectRatio="none"
      role="img"
      :aria-label="t('CAMPAIGN.WHATSAPP.ANALYTICS.BREAKDOWN.TITLE')"
      class="w-full h-2 rounded-full"
    >
      <defs>
        <clipPath id="campaign-delivery-breakdown-clip">
          <rect width="100" height="8" rx="4" />
        </clipPath>
      </defs>
      <g clip-path="url(#campaign-delivery-breakdown-clip)">
        <rect
          v-for="segment in chartSegments"
          :key="segment.key"
          :x="segment.offset"
          y="0"
          :width="segment.width"
          height="8"
          :class="segment.barClass"
        />
      </g>
    </svg>
    <div v-else class="w-full h-2 rounded-full bg-n-alpha-2" />
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <div
        v-for="segment in segments"
        :key="segment.key"
        class="flex items-center min-w-0 gap-2"
      >
        <span class="rounded-full size-2 shrink-0" :class="segment.dotClass" />
        <span class="truncate text-body-main text-n-slate-11">
          {{ segment.label }}
        </span>
        <span class="font-medium tabular-nums text-body-main text-n-slate-12">
          {{ segment.value }}
        </span>
      </div>
    </div>
  </div>
</template>
